import { useRef, useState } from "react";
import { CounterpartyAddress } from "@common/schemas/counterpartyAddress.schema";
import { CreateCounterpartyAddressBody } from "@common/schemas/counterpartyAddress.schema";
import { CounterpartyContact } from "@common/schemas/counterpartyContact.schema";
import { CreateCounterpartyContactBody } from "@common/schemas/counterpartyContact.schema";
import {
    useCreateCounterpartyAddressMutation,
    useCreateCounterpartyContactMutation,
} from "@api/counterparty.api";

const SEED_EPOCH = new Date(0);

interface SeedRow<TBody> {
    id: string;
    body: TBody;
    isSuggested: boolean;
    replacesId?: string | null;
}

export interface LocalBookHandlers<TBody> {
    save: (id: string, body: TBody) => void;
    remove: (id: string) => void;
    makePrimary: (id: string) => void;
}

function seedRows<TBody extends { isPrimary?: boolean }>(
    prefix: string,
    bodies: TBody[],
    replacesIds: ReadonlyArray<string | null> = [],
): Array<SeedRow<TBody>> {
    return bodies.map((body, index) => ({
        id: `${prefix}-${index}`,
        body,
        isSuggested: true,
        replacesId: replacesIds[index] ?? null,
    }));
}

function saveSeed<TBody extends { isPrimary?: boolean }>(
    rows: Array<SeedRow<TBody>>,
    id: string,
    body: TBody,
): Array<SeedRow<TBody>> {
    return rows.map((row) =>
        row.id === id
            ? {
                  ...row,
                  isSuggested: false,
                  body: {
                      ...body,
                      isPrimary: body.isPrimary ?? row.body.isPrimary,
                  },
              }
            : row,
    );
}

function makePrimarySeed<TBody extends { isPrimary?: boolean }>(
    rows: Array<SeedRow<TBody>>,
    id: string,
): Array<SeedRow<TBody>> {
    return rows.map((row) => ({
        ...row,
        body: { ...row.body, isPrimary: row.id === id },
    }));
}

export function removeSeed<TBody extends { isPrimary?: boolean }>(
    rows: Array<SeedRow<TBody>>,
    id: string,
): Array<SeedRow<TBody>> {
    const isRemovingPrimary = rows.some(
        (row) => row.id === id && row.body.isPrimary && !row.replacesId,
    );
    const remaining = rows.filter((row) => row.id !== id);
    if (!isRemovingPrimary) {
        return remaining;
    }
    return remaining.map((row, index) =>
        index === 0 ? { ...row, body: { ...row.body, isPrimary: true } } : row,
    );
}

function contactRow(
    row: SeedRow<CreateCounterpartyContactBody>,
): CounterpartyContact {
    return {
        id: row.id,
        counterpartyId: "",
        label: row.body.label ?? null,
        isPrimary: row.body.isPrimary ?? false,
        title: row.body.title ?? null,
        firstName: row.body.firstName ?? null,
        middleName: row.body.middleName ?? null,
        lastName: row.body.lastName ?? null,
        suffix: row.body.suffix ?? null,
        email: row.body.email ?? null,
        phoneNumber: row.body.phoneNumber ?? null,
        createdAt: SEED_EPOCH,
        updatedAt: SEED_EPOCH,
    };
}

function addressRow(
    row: SeedRow<CreateCounterpartyAddressBody>,
): CounterpartyAddress {
    return {
        id: row.id,
        counterpartyId: "",
        label: row.body.label ?? null,
        isPrimary: row.body.isPrimary ?? false,
        street: row.body.street ?? null,
        street2: row.body.street2 ?? null,
        city: row.body.city ?? null,
        postalCode: row.body.postalCode ?? null,
        state: row.body.state ?? null,
        country: row.body.country ?? null,
        createdAt: SEED_EPOCH,
        updatedAt: SEED_EPOCH,
    };
}

function isPreviewRow<TBody>(row: SeedRow<TBody>): boolean {
    return row.isSuggested || Boolean(row.replacesId);
}

function replacementsOf<TBody>(
    rows: Array<SeedRow<TBody>>,
): Array<{ replacesId: string; body: TBody }> {
    return rows.flatMap((row) =>
        row.replacesId ? [{ replacesId: row.replacesId, body: row.body }] : [],
    );
}

export function useOcrSeedBooks({
    contacts: contactBodies,
    addresses: addressBodies,
    contactReplacesIds = [],
    addressReplacesIds = [],
}: {
    contacts: CreateCounterpartyContactBody[];
    addresses: CreateCounterpartyAddressBody[];
    contactReplacesIds?: ReadonlyArray<string | null>;
    addressReplacesIds?: ReadonlyArray<string | null>;
}) {
    const [contacts, setContacts] = useState(() =>
        seedRows("ocr-contact", contactBodies, contactReplacesIds),
    );
    const [addresses, setAddresses] = useState(() =>
        seedRows("ocr-address", addressBodies, addressReplacesIds),
    );
    const createContact = useCreateCounterpartyContactMutation();
    const createAddress = useCreateCounterpartyAddressMutation();
    const persistPromiseRef = useRef<Promise<boolean> | null>(null);

    const persistSeeds = async (counterpartyId: string) => {
        const contactCreates = contacts.filter((row) => !row.replacesId);
        const addressCreates = addresses.filter((row) => !row.replacesId);
        const contactWrites = contactCreates.map((row) =>
            createContact.mutateAsync({ counterpartyId, body: row.body }),
        );
        const addressWrites = addressCreates.map((row) =>
            createAddress.mutateAsync({ counterpartyId, body: row.body }),
        );
        const results = await Promise.allSettled([
            ...contactWrites,
            ...addressWrites,
        ]);
        const failedContactIds = contactCreates.flatMap((row, index) =>
            results[index]?.status === "rejected" ? [row.id] : [],
        );
        const failedAddressIds = addressCreates.flatMap((row, index) =>
            results[contactCreates.length + index]?.status === "rejected"
                ? [row.id]
                : [],
        );
        setContacts((rows) =>
            rows.filter(
                (row) =>
                    Boolean(row.replacesId) ||
                    failedContactIds.includes(row.id),
            ),
        );
        setAddresses((rows) =>
            rows.filter(
                (row) =>
                    Boolean(row.replacesId) ||
                    failedAddressIds.includes(row.id),
            ),
        );
        return results.some((result) => result.status === "rejected");
    };

    const persist = (counterpartyId: string) => {
        if (persistPromiseRef.current) {
            return persistPromiseRef.current;
        }

        const promise = persistSeeds(counterpartyId);
        persistPromiseRef.current = promise;
        return promise;
    };

    const contactHandlers: LocalBookHandlers<CreateCounterpartyContactBody> = {
        save: (id, body) => setContacts((rows) => saveSeed(rows, id, body)),
        remove: (id) => setContacts((rows) => removeSeed(rows, id)),
        makePrimary: (id) => setContacts((rows) => makePrimarySeed(rows, id)),
    };
    const addressHandlers: LocalBookHandlers<CreateCounterpartyAddressBody> = {
        save: (id, body) => setAddresses((rows) => saveSeed(rows, id, body)),
        remove: (id) => setAddresses((rows) => removeSeed(rows, id)),
        makePrimary: (id) => setAddresses((rows) => makePrimarySeed(rows, id)),
    };

    return {
        contactRows: contacts.map(contactRow),
        addressRows: addresses.map(addressRow),
        contactReplacements: replacementsOf(contacts),
        addressReplacements: replacementsOf(addresses),
        contactRowReplacesIds: contacts.map((row) => row.replacesId ?? null),
        addressRowReplacesIds: addresses.map((row) => row.replacesId ?? null),
        suggestedContactIds: contacts.flatMap((row) =>
            isPreviewRow(row) ? [row.id] : [],
        ),
        suggestedAddressIds: addresses.flatMap((row) =>
            isPreviewRow(row) ? [row.id] : [],
        ),
        contactHandlers,
        addressHandlers,
        persist,
    };
}
